/**
 * Employer Setup Settings Component
 * Allows organizations to set up or link themselves as an employer organization
 * for the Employee Navigation Platform
 */

import React, { useState, useEffect } from 'react';
import {
  Briefcase,
  Building,
  Users,
  CheckCircle,
  AlertCircle,
  Loader2,
  Link2,
  Unlink
} from 'lucide-react';
import { apiClient } from '@/services/apiClient';
import { useAppTranslation } from '@/contexts/TranslationContext';

interface EmployerData {
  id: number;
  employer_number: string;
  company_name: string;
  legal_entity_name?: string;
  company_size_tier?: string;
  employee_count?: number;
  contract_status: string;
  hris_system?: string;
  hris_sync_status?: string;
  is_active: boolean;
  cbo_id: number;
}

interface EmployerSetupSettingsProps {
  organizationId: number;
  organizationName: string;
  onSetupComplete?: (employerId: number) => void;
}

export function EmployerSetupSettings({
  organizationId,
  organizationName,
  onSetupComplete
}: EmployerSetupSettingsProps) {
  const { t } = useAppTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [employerData, setEmployerData] = useState<EmployerData | null>(null);
  const [isLinked, setIsLinked] = useState(false);

  // Form state for creating new employer
  const [formData, setFormData] = useState({
    company_name: organizationName,
    legal_entity_name: '',
    company_size_tier: 'MEDIUM',
    employee_count: 100,
    contract_status: 'ACTIVE',
    hris_system: '',
  });

  // Check if organization is already linked to an employer
  useEffect(() => {
    const checkEmployerStatus = async () => {
      try {
        setLoading(true);
        setError(null);

        // Try to find an employer linked to this CBO
        const response = await apiClient.get(`/api/employers/by-cbo/${organizationId}`);
        if (response.data) {
          setEmployerData(response.data);
          setIsLinked(true);
        }
      } catch (err: any) {
        // 404 means no employer linked, which is fine
        if (err.response?.status !== 404) {
          console.error('Error checking employer status:', err);
        }
        setIsLinked(false);
      } finally {
        setLoading(false);
      }
    };

    checkEmployerStatus();
  }, [organizationId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'employee_count' ? parseInt(value) || 0 : value
    }));
  };

  const handleCreateEmployer = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const payload = {
        ...formData,
        cbo_id: organizationId,
      };

      const response = await apiClient.post('/api/employers', payload);

      setEmployerData(response.data);
      setIsLinked(true);
      setSuccess('Organization has been set up as an employer successfully!');

      // Update localStorage to reflect employer status
      localStorage.setItem('active_employer_id', String(response.data.id));

      if (onSetupComplete) {
        onSetupComplete(response.data.id);
      }
    } catch (err: any) {
      console.error('Error creating employer:', err);
      setError(err.response?.data?.detail || 'Failed to set up employer organization');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlinkEmployer = async () => {
    if (!employerData) return;

    if (!window.confirm('Are you sure you want to unlink this organization from the employer record? This will disable employer features.')) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Deactivate the employer (soft delete)
      await apiClient.patch(`/api/employers/${employerData.id}`, {
        is_active: false
      });

      setEmployerData(null);
      setIsLinked(false);
      setSuccess('Employer link has been removed.');

      // Clear employer ID from localStorage
      localStorage.removeItem('active_employer_id');
    } catch (err: any) {
      console.error('Error unlinking employer:', err);
      setError(err.response?.data?.detail || 'Failed to unlink employer');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Checking employer status...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <Briefcase className="w-6 h-6 text-blue-600" />
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('settings.employer_setup', 'Employer Organization Setup')}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Configure this organization as an employer to enable employee navigation features
          </p>
        </div>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      {isLinked && employerData ? (
        /* Employer Linked View */
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="font-medium text-green-700">Employer Organization Active</span>
            </div>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
              employerData.contract_status === 'ACTIVE'
                ? 'bg-green-100 text-green-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}>
              {employerData.contract_status}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="text-xs text-gray-500">Employer Number</label>
              <p className="font-medium text-gray-900 dark:text-white">{employerData.employer_number}</p>
            </div>
            <div>
              <label className="text-xs text-gray-500">Company Name</label>
              <p className="font-medium text-gray-900 dark:text-white">{employerData.company_name}</p>
            </div>
            <div>
              <label className="text-xs text-gray-500">Company Size</label>
              <p className="font-medium text-gray-900 dark:text-white">
                {employerData.company_size_tier || 'Not specified'}
              </p>
            </div>
            <div>
              <label className="text-xs text-gray-500">Employee Count</label>
              <p className="font-medium text-gray-900 dark:text-white">
                {employerData.employee_count?.toLocaleString() || 'Not specified'}
              </p>
            </div>
            <div>
              <label className="text-xs text-gray-500">HRIS System</label>
              <p className="font-medium text-gray-900 dark:text-white">
                {employerData.hris_system || 'Not connected'}
              </p>
            </div>
            <div>
              <label className="text-xs text-gray-500">HRIS Sync Status</label>
              <p className="font-medium text-gray-900 dark:text-white">
                {employerData.hris_sync_status || 'Not configured'}
              </p>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleUnlinkEmployer}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Unlink className="w-4 h-4" />
              )}
              Unlink Employer
            </button>
          </div>
        </div>
      ) : (
        /* Setup Form View */
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Building className="w-5 h-5 text-gray-600" />
            <span className="font-medium text-gray-700 dark:text-gray-300">
              Set Up as Employer Organization
            </span>
          </div>

          <p className="text-sm text-gray-500 mb-6">
            Setting up this organization as an employer will enable:
          </p>

          <ul className="mb-6 space-y-2">
            <li className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <CheckCircle className="w-4 h-4 text-blue-600" />
              Employee roster management and HRIS integration
            </li>
            <li className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <CheckCircle className="w-4 h-4 text-blue-600" />
              Employee self-service portal
            </li>
            <li className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <CheckCircle className="w-4 h-4 text-blue-600" />
              Benefits program management
            </li>
            <li className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <CheckCircle className="w-4 h-4 text-blue-600" />
              AI-powered navigation for employees
            </li>
          </ul>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Company Name
              </label>
              <input
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Legal Entity Name (optional)
              </label>
              <input
                type="text"
                name="legal_entity_name"
                value={formData.legal_entity_name}
                onChange={handleInputChange}
                placeholder="If different from company name"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Company Size
                </label>
                <select
                  name="company_size_tier"
                  value={formData.company_size_tier}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="SMALL">Small (1-49)</option>
                  <option value="MEDIUM">Medium (50-249)</option>
                  <option value="LARGE">Large (250-999)</option>
                  <option value="ENTERPRISE">Enterprise (1000+)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Estimated Employee Count
                </label>
                <input
                  type="number"
                  name="employee_count"
                  value={formData.employee_count}
                  onChange={handleInputChange}
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                HRIS System (optional)
              </label>
              <select
                name="hris_system"
                value={formData.hris_system}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">Not using HRIS</option>
                <option value="WORKDAY">Workday</option>
                <option value="ADP">ADP</option>
                <option value="UKG">UKG (Ultimate Kronos Group)</option>
                <option value="BAMBOOHR">BambooHR</option>
                <option value="PAYLOCITY">Paylocity</option>
                <option value="PAYCHEX">Paychex</option>
                <option value="NAMELY">Namely</option>
                <option value="RIPPLING">Rippling</option>
                <option value="GUSTO">Gusto</option>
                <option value="ZENEFITS">Zenefits</option>
                <option value="CUSTOM_API">Custom API</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end mt-6">
            <button
              onClick={handleCreateEmployer}
              disabled={saving || !formData.company_name}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Link2 className="w-4 h-4" />
              )}
              Set Up as Employer
            </button>
          </div>
        </div>
      )}

      {/* Information Section */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Users className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900 dark:text-blue-100">
              About Employer Organizations
            </h4>
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
              Employer organizations can manage employee benefit programs, integrate with HRIS systems,
              and provide personalized navigation for their workforce. This is typically used by
              self-insured employers or companies that want to offer benefit navigation to their employees.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmployerSetupSettings;
